import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { AlertTriangle, Building2, Check, IdCard, Info, Languages, Link2, Lock, Pencil, Plus, Sparkles, Unlock, UserPlus, X } from "lucide-react"
import { ConfirmableEmailField } from "../fields/confirmable-email-field"
import { FIELD_ANCHOR_ID_PREFIX } from "../constants"
import type {
  PlacementFormState,
  PlacementFormErrors,
  AtsSourceValues,
  ProfileType,
  WorkerProfile,
  WorkerScenario,
} from "../types"
import type { SearchableSelectOption } from "../fields/searchable-select"
import { AtsHint, useFormatContactStatus, contactStatusColor } from "../fields/display-helpers"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/translations"
import { SearchableSelect } from "../fields/searchable-select"
import type { ContactOption } from "../placement-details-content"
import { useConfirmCreateContact } from "@/hooks/use-confirm-create-contact"
import { usePendingProfileTypeChange } from "@/hooks/use-pending-profile-type-change"
import { useConfirmableEmailEdit } from "@/hooks/use-confirmable-email-edit"
import { useNewWorkerOrgEditor } from "@/hooks/use-new-worker-org-editor"
import { usePreferredLanguage } from "@/hooks/use-preferred-language"
import type { PreVerificationDecision, PreVerificationState } from "@/api/onboarding-api.types"
import { PreVerificationField } from "../fields/pre-verification-field"

interface AsyncSearch {
  search: (term: string) => void
  loading: boolean
}

interface OrgSearchHook extends AsyncSearch {
  options: SearchableSelectOption[]
}

export interface WorkerSetupSectionProps {
  form: PlacementFormState
  errors: PlacementFormErrors
  showError: (field: keyof PlacementFormErrors) => boolean | undefined
  atsSource: AtsSourceValues
  profileTypes: ProfileType[]
  contactOptions: ContactOption[]
  selectedContactId: string | null
  hasNameMismatch: boolean
  isStatusBlocked: boolean
  blockedProfileUrl: string | null
  workerLoading?: boolean
  workerOrgSearch: OrgSearchHook
  workerOrgRefreshKey?: number
  isWorkerOrgRequired: boolean
  isWorkerOrgVisible: boolean
  isWorkerOrgLocked: boolean
  /** True when the selected worker profile already has an associated organization. We then suppress the "Create new" affordance — that worker is already linked to an org. */
  selectedWorkerHasOrg: boolean
  /** UI mode for the Worker Organization section. "search" reveals the org selector; "unknown" shows the placeholder-creation banner. */
  workerOrgInputMode: "search" | "unknown"
  /** True when the user is allowed to flip into "I don't know" mode (the radio toggle should
   * be visible). Tracks the same conditions as the hook's submit-time placeholder swap:
   * create-contact, or add-profile after manual unlock. */
  workerOrgCanChooseUnknownMode: boolean
  /** Org IDs already linked to a same-type profile in this email's worker landscape. Picking
   * one would link a new profile to an org that already has the same kind for this worker —
   * so the section blocks the selection with a warning + "Use existing profile" affordance. */
  workerOrgExcludedIds: Set<string>
  /** Preview of the placeholder org name that will be auto-created if the user submits in "unknown" mode. Null when no worker name is resolvable yet. */
  workerOrgPlaceholderName: string | null
  onUnlockWorkerOrg: () => void
  changedFields?: {
    profileTypeChanged?: boolean
    workerChanged?: boolean
    workerOrgChanged?: boolean
    emailChanged?: boolean
  }
  workerScenario: WorkerScenario | null
  showCreateContactAction: boolean
  /** True when the email's existing contacts already have a profile of the selected profile
   * type. When true, the "Add new worker contact" button is hidden and an explainer is shown
   * in its place. */
  hasExistingProfileOfSelectedType: boolean
  onSelectContact: (contactId: string) => void
  onCreateContact: () => void
  onUndoWorkerCreation: () => void
  onEmailConfirm: (email: string) => void
  /** Forwarded to ConfirmableEmailField so the form can block submission while the email has un-confirmed edits open. */
  onEmailEditingChange: (isEditing: boolean) => void
  onProfileTypeChange: (code: string | null) => void
  onSelectWorkerOrg: (orgId: string) => void
  /** Switch directly into existing-profile scenario for the given org's matching profile.
   * Wired to the hook's `useExistingProfileForOrg` so the user can resolve the "this org is
   * already linked to a same-type profile" warning with one click. */
  onUseExistingProfileForOrg: (orgId: string) => void
  onCreateWorkerOrg: (name: string) => void
  /** Switch the Worker Organization section to "search" mode (reveal the org selector). */
  onEnterWorkerOrgSearchMode: () => void
  /** Switch the Worker Organization section to "I don't know" mode (placeholder banner). */
  onEnterWorkerOrgUnknownMode: () => void
  onClearWorkerOrg: () => void
  sourceLabel?: string
  /** Two-letter culture code (e.g. "en", "fr") to apply to a newly created contact. Editable
   * for create-contact scenarios; display-only for existing contacts (the contact's preferred
   * language lives in FBO and the value here is ignored at submit). */
  workerPreferredCultureCode: string
  onWorkerPreferredCultureCodeChange: (code: string) => void
  /** All worker profiles loaded for this email — used to resolve the FBO contact deep-link
   * when the user picks an existing contact (existing-profile / add-profile scenarios). */
  workerAllProfiles: WorkerProfile[]
  /** Base URL for the FBO web app (e.g. https://uat.flexbackoffice.com). When falsy the
   * "Open in FBO" link is suppressed and only the explanatory message is shown. */
  fboAppUrl?: string
  isPreVerificationVisible: boolean
  /** The pre-verification linked to the source when it holds up onboarding. Null when there is none to show. */
  preVerification: PreVerificationState | null
  preVerificationDecision: PreVerificationDecision | null
  onPreVerificationDecisionChange: (decision: PreVerificationDecision | null) => void
  isPreVerificationRefreshing: boolean
  onRefreshPreVerification: () => void
  onViewPreVerification: () => void
  isPreVerificationUpdatingFromReview: boolean
  preVerificationUpdateFromReviewFailed: boolean
  onRetryPreVerificationUpdateFromReview: () => void
  wasPreVerificationUpdatedFromReview: boolean
}

export function WorkerSetupSection({
  form,
  errors,
  showError,
  atsSource: ats,
  profileTypes,
  contactOptions,
  selectedContactId,
  hasNameMismatch,
  isStatusBlocked,
  blockedProfileUrl,
  workerLoading,
  workerOrgSearch,
  workerOrgRefreshKey,
  isWorkerOrgRequired,
  isWorkerOrgVisible,
  isWorkerOrgLocked,
  selectedWorkerHasOrg,
  workerOrgInputMode,
  workerOrgCanChooseUnknownMode,
  workerOrgExcludedIds,
  workerOrgPlaceholderName,
  onUnlockWorkerOrg,
  changedFields,
  workerScenario,
  showCreateContactAction,
  hasExistingProfileOfSelectedType,
  onSelectContact,
  onCreateContact,
  onUndoWorkerCreation,
  onEmailConfirm,
  onEmailEditingChange,
  onProfileTypeChange,
  onSelectWorkerOrg,
  onUseExistingProfileForOrg,
  onCreateWorkerOrg,
  onEnterWorkerOrgSearchMode,
  onEnterWorkerOrgUnknownMode,
  onClearWorkerOrg,
  sourceLabel,
  workerPreferredCultureCode,
  onWorkerPreferredCultureCodeChange,
  workerAllProfiles,
  fboAppUrl,
  isPreVerificationVisible,
  preVerification,
  preVerificationDecision,
  onPreVerificationDecisionChange,
  isPreVerificationRefreshing,
  onRefreshPreVerification,
  onViewPreVerification,
  isPreVerificationUpdatingFromReview,
  preVerificationUpdateFromReviewFailed,
  onRetryPreVerificationUpdateFromReview,
  wasPreVerificationUpdatedFromReview,
}: WorkerSetupSectionProps) {
  const { t } = useTranslation()
  const formatContactStatus = useFormatContactStatus()
  const {
    profileTypeChanged,
    workerChanged,
    workerOrgChanged,
    emailChanged,
  } = changedFields ?? {}

  const workerOrgScenario = form.workerOrgScenario
  const workerOrgId = workerOrgScenario?.kind === "existing" ? workerOrgScenario.organizationId : null
  const newWorkerOrgName = workerOrgScenario?.kind === "new" ? workerOrgScenario.organizationName : null
  const isWorkerOrgUnknown = workerOrgInputMode === "unknown"

  const createContact = useConfirmCreateContact({
    workerScenario,
    contactOptionsCount: contactOptions.length,
    onCreateContact,
  })

  const profileTypeChange = usePendingProfileTypeChange({
    currentProfileTypeCode: form.profileTypeCode,
    workerScenario,
    onProfileTypeChange,
  })

  const emailEdit = useConfirmableEmailEdit({
    workerScenario,
    contactOptionsCount: contactOptions.length,
  })

  // All Preferred Language section logic — mode gate (readonly when FBO has a contact
  // for this email, editable otherwise), FBO deep-link resolution, mismatch detection,
  // and the pending-change confirmation gate — lives in usePreferredLanguage so the JSX
  // below stays presentational.
  const language = usePreferredLanguage({
    workerScenario,
    workerAllProfiles,
    currentValue: workerPreferredCultureCode,
    onCommit: onWorkerPreferredCultureCodeChange,
    fboAppUrl,
  })

  // Suppress the "Create new" affordance only while the field is locked. We used to also
  // suppress it when the selected worker profile already had an org, but that left users no
  // way to override the org after they explicitly clicked the unlock button. The hook
  // transitions the scenario to add-profile when the user creates a new org from this state,
  // so the new org actually gets created against a new profile (the existing profile's org
  // link can't be updated via this drawer).
  const suppressCreateNewWorkerOrg = isWorkerOrgLocked
  const showBlockedBanner =
    isStatusBlocked && workerScenario != null && "contactId" in workerScenario

  const workerSelectedLabel = workerScenario?.kind === "create-contact"
    ? ats.workerName
    : undefined

  return (
    <section>
      <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3">
        {t("sectionWorkerSetup")}
      </h3>
      <div className="flex flex-col gap-1">
        {/* Email address */}
        <div id={`${FIELD_ANCHOR_ID_PREFIX}workerEmail`} className="flex flex-col gap-1.5">
          <ConfirmableEmailField
            value={form.workerEmail}
            onConfirm={onEmailConfirm}
            onAttemptStartEdit={emailEdit.onAttemptStartEdit}
            forceStartEditingTrigger={emailEdit.forceEditTrigger}
            onEditingChange={onEmailEditingChange}
            error={
              showError("workerEmail")
                ? errors.workerEmail
                : undefined
            }
            required
            atsHint={<AtsHint value={ats.workerEmail} />}
            changedPill={emailChanged}
          />
          {emailEdit.pendingEmailEdit && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-blue-50 border border-blue-300">
              <Info size={14} className="text-blue-600 shrink-0 mt-0.5" />
              <div className="flex-1 flex flex-col gap-2">
                <p className="text-xs text-blue-800 leading-relaxed">
                  {(() => {
                    const [before, after = ""] = t("editingEmailWillDiscard").split("{discard}")
                    return (
                      <>
                        {before}
                        <span className="font-semibold text-red-700">{t("discard")}</span>
                        {after}
                      </>
                    )
                  })()}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={emailEdit.confirm}
                    className="text-xs font-medium px-2.5 py-1 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                  >
                    {t("yesEditEmail")}
                  </button>
                  <button
                    type="button"
                    onClick={emailEdit.cancel}
                    className="text-xs font-medium px-2.5 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    {t("cancel")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {isPreVerificationVisible && preVerification && (
          <PreVerificationField
            state={preVerification}
            decision={preVerificationDecision}
            onDecisionChange={onPreVerificationDecisionChange}
            error={showError("preVerification") ? errors.preVerification : undefined}
            isRefreshing={isPreVerificationRefreshing}
            onRefresh={onRefreshPreVerification}
            onView={onViewPreVerification}
            isUpdatingFromReview={isPreVerificationUpdatingFromReview}
            updateFromReviewFailed={preVerificationUpdateFromReviewFailed}
            onRetryUpdateFromReview={onRetryPreVerificationUpdateFromReview}
            wasUpdatedFromReview={wasPreVerificationUpdatedFromReview}
          />
        )}

        {/* Preferred Language — Contact-level attribute in FBO (one value per contact). The
            decision is data-driven: as soon as the email-based FBO search returns at least
            one matching Worker profile, we know the contact already exists and FBO's
            Contact.CultureId is the canonical preferred language — the dropdown becomes
            read-only regardless of which scenario the user later picks. When no contact is
            found yet, the section is editable and the chosen value is applied at submit
            time (via WorkerScenario.preferredLanguage on the create-contact branch). */}
        {language.mode === "readonly" ? (
          <div className="flex flex-col mb-4">
            <SearchableSelect
              label={t("labelPreferredLanguage")}
              value={language.displayCode}
              onValueChange={() => {}}
              options={language.languageOptions}
              hideSearch
              disabled
            />
            {/* -mt-3 closes the gap left by SearchableSelect's empty helper-text slot so
                the warning sits tight against the dropdown. */}
            <div className="-mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-300">
              <Languages size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 flex flex-col gap-1.5">
                <p className="text-xs text-amber-800 leading-relaxed">
                  <ReadOnlyLanguageWarning
                    fboCultureCode={language.fboCultureCode}
                    requestCultureCode={language.requestCultureCode}
                    hasMismatch={language.hasMismatch}
                    labelFor={language.labelFor}
                    t={t}
                  />
                </p>
                {language.fboLink && (
                  <a
                    href={language.fboLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="self-start text-xs font-medium text-blue-600 hover:text-blue-700 underline underline-offset-2"
                  >
                    {t("openContactInFbo")}
                  </a>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col mb-4">
            <SearchableSelect
              label={t("labelPreferredLanguage")}
              value={workerPreferredCultureCode}
              onValueChange={language.attemptChange}
              options={language.languageOptions}
              hideSearch
            />
            {/* -mt-3 closes the gap left by SearchableSelect's empty helper-text slot. */}
            <div className="-mt-3 flex flex-col gap-1.5">
              {/* Confirm panel renders FIRST when a different option is pending, so the
                  user's attention lands on it before the permanent banner. */}
              {language.pendingValue && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-blue-50 border border-blue-300">
                  <Info size={14} className="text-blue-600 shrink-0 mt-0.5" />
                  <div className="flex-1 flex flex-col gap-2">
                    <p className="text-xs font-medium text-blue-900">
                      {t("preferredLanguageChangeConfirmTitle")}
                    </p>
                    <p className="text-xs text-blue-800 leading-relaxed">
                      <PendingLanguageChangeBody
                        currentCode={workerPreferredCultureCode}
                        pendingCode={language.pendingValue}
                        labelFor={language.labelFor}
                        t={t}
                      />
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={language.confirm}
                        className="text-xs font-medium px-2.5 py-1 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                      >
                        {t("yesChangeLanguage")}
                      </button>
                      <button
                        type="button"
                        onClick={language.cancel}
                        className="text-xs font-medium px-2.5 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        {t("cancel")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* Permanent confirmation banner — sits below the optional confirm panel so
                  the transient confirmation takes visual priority. */}
              <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-300">
                <Languages size={14} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  {t("preferredLanguageConfirmWarning")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Profile Type — destructive when a worker is already selected, since the scenario
            re-derives (and may switch contacts or drop into add-profile, losing the prior
            worker/org selection). When that's the case, intercept the click and surface an
            inline confirmation; otherwise commit immediately. */}
        <div id={`${FIELD_ANCHOR_ID_PREFIX}profileTypeCode`} className="flex flex-col gap-1.5">
          <SearchableSelect
            label={t("labelProfileType")}
            value={form.profileTypeCode}
            onValueChange={profileTypeChange.attemptChange}
            options={profileTypes.map((pt) => ({
              id: pt.code,
              name: pt.value,
            }))}
            placeholder={t("placeholderSelectProfileType")}
            error={
              showError("profileTypeCode")
                ? errors.profileTypeCode
                : undefined
            }
            required
            atsHint={
              <AtsHint
                value={
                  ats.profileTypeCode
                    ? profileTypes.find(
                        (pt) => pt.code === ats.profileTypeCode
                      )?.value ?? ats.profileTypeCode
                    : null
                }
              />
            }
            changedPill={profileTypeChanged}
          />
          {profileTypeChange.pendingProfileTypeCode && (() => {
            const pending = profileTypeChange.pendingProfileTypeCode
            const pendingLabel = profileTypes.find((pt) => pt.code === pending)?.value ?? pending
            return (
              <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-blue-50 border border-blue-300">
                <Info size={14} className="text-blue-600 shrink-0 mt-0.5" />
                <div className="flex-1 flex flex-col gap-2">
                  <p className="text-xs text-blue-800 leading-relaxed">
                    {(() => {
                      const raw = t("switchingProfileTypeWillDiscard", { profileType: "@@PT@@" })
                      const [beforePT, rest = ""] = raw.split("@@PT@@")
                      const [middle, after = ""] = rest.split("{discard}")
                      return (
                        <>
                          {beforePT}
                          <span className="font-semibold text-blue-900">{`"${pendingLabel}"`}</span>
                          {middle}
                          <span className="font-semibold text-red-700">{t("discard")}</span>
                          {after}
                        </>
                      )
                    })()}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={profileTypeChange.confirm}
                      className="text-xs font-medium px-2.5 py-1 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                    >
                      {t("yesSwitch")}
                    </button>
                    <button
                      type="button"
                      onClick={profileTypeChange.cancel}
                      className="text-xs font-medium px-2.5 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      {t("cancel")}
                    </button>
                  </div>
                </div>
              </div>
            )
          })()}
        </div>

        {/* Worker (contact-level dropdown or creation banner) */}
        {(!form.workerEmail || !form.profileTypeCode) ? (
          <div id={`${FIELD_ANCHOR_ID_PREFIX}worker`} className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium text-gray-700">
              {t("labelWorker")}
              <span className="text-red-500 ml-0.5">*</span>
            </Label>
            <div className="flex items-center gap-2 h-9 px-3 rounded-md border border-dashed border-gray-200 bg-gray-50">
              <Lock size={14} className="text-amber-500 shrink-0" />
              <span className="text-sm text-gray-400">
                {!form.workerEmail
                  ? t("enterEmailFirst")
                  : t("selectProfileTypeFirst")}
              </span>
            </div>
            <p className="min-h-4" />
          </div>
        ) : (
        <div id={`${FIELD_ANCHOR_ID_PREFIX}worker`} className="flex flex-col gap-1.5">
          <SearchableSelect
            label={t("labelWorker")}
            value={selectedContactId}
            selectedLabel={workerSelectedLabel}
            onValueChange={(v) => {
              onSelectContact(v)
            }}
            loading={workerLoading}
            options={contactOptions}
            placeholder={t("placeholderSelectWorker")}

            hideSearch
            error={
              showError("worker")
                ? errors.worker
                : undefined
            }
            required
            atsHint={<AtsHint value={ats.workerName} />}
            changedPill={workerChanged}
            creatingLabel={
              workerScenario?.kind === "create-contact"
                ? ats.workerName
                  ? `${ats.workerName} (${form.workerEmail})`
                  : form.workerEmail
                : undefined
            }
            onUndoCreate={onUndoWorkerCreation}
            creatingWarning={
              workerScenario?.kind === "create-contact" && contactOptions.length > 0
                ? t("creatingContactExistingFound", { count: contactOptions.length })
                : undefined
            }
            renderOption={(option) => {
              const contact = contactOptions.find((c) => c.id === option.id)
              if (!contact) return <span>{option.name}</span>
              return (
                <div className="flex flex-col gap-1 py-0.5 w-full">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {contact.name}
                    </span>
                    <span className={cn(
                      "inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ring-1 ring-inset",
                      contactStatusColor(contact.contactStatus)
                    )}>
                      {formatContactStatus(contact.contactStatus)}
                    </span>
                    <span className="ml-auto text-xs text-gray-400 font-mono">
                      {contact.contactId}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {contact.profileTypeCodes.map((code) => {
                      const pt = profileTypes.find((p) => p.code === code)
                      return (
                        <span
                          key={code}
                          className="text-xs px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200"
                        >
                          {pt?.value ?? code}
                        </span>
                      )
                    })}
                  </div>
                </div>
              )
            }}
          >
            {/* Create new contact action.
                When existing contacts already match the email, intercept the first click with
                an inline confirmation prompt. The confirmation copy escalates when one of
                those contacts already has a profile of the selected type, since duplicate
                same-type profiles are the most common foot-gun (still allowed — the user may
                legitimately want a new profile linked to a different organization). */}
            {showCreateContactAction && !createContact.isConfirming && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <button
                  type="button"
                  onClick={createContact.attemptCreate}
                  className="w-full text-left px-2 py-2 rounded-md text-sm flex items-center gap-2 hover:bg-blue-50 transition-colors text-blue-700"
                >
                  <Plus size={14} className="shrink-0" />
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium">
                      {t("addNewWorkerContact")}
                    </span>
                    {(ats.workerName || form.workerEmail) && (
                      <span className="text-[10px] text-gray-400">
                        {ats.workerName && form.workerEmail
                          ? `${ats.workerName} · ${form.workerEmail}`
                          : (ats.workerName || form.workerEmail)}
                      </span>
                    )}
                  </div>
                </button>
              </>
            )}
            {showCreateContactAction && createContact.isConfirming && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <div className="m-1 p-3 rounded-md bg-blue-50 border border-blue-300">
                  <div className="flex items-start gap-2">
                    <Info size={14} className="text-blue-600 shrink-0 mt-0.5" />
                    <div className="flex-1 flex flex-col gap-2">
                      <div className="flex flex-col gap-1">
                        {hasExistingProfileOfSelectedType ? (
                          <>
                            <p className="text-xs font-medium text-blue-900">
                              {(() => {
                                const ptLabel = profileTypes.find((pt) => pt.code === form.profileTypeCode)?.value ?? form.profileTypeCode ?? ""
                                const [before, after = ""] = t("existingContactWithProfileTitle", { profileType: "@@PT@@" }).split("@@PT@@")
                                return (
                                  <>
                                    {before}
                                    <span className="px-1 py-0.5 rounded bg-blue-100 text-blue-900">{ptLabel}</span>
                                    {after}
                                  </>
                                )
                              })()}
                            </p>
                            <p className="text-xs text-blue-800 leading-relaxed">
                              {t("existingContactWithProfileBody")}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-xs font-medium text-blue-900">
                              {(() => {
                                const badge = t("existingContactsBadge", { count: contactOptions.length, plural: contactOptions.length === 1 ? "" : "s" })
                                const [before, after = ""] = t("existingContactsMatchEmail").split("{badge}")
                                return (
                                  <>
                                    {before}
                                    <span className="px-1 py-0.5 rounded bg-blue-100 text-blue-900 font-semibold">{badge}</span>
                                    {after}
                                  </>
                                )
                              })()}
                            </p>
                            <p className="text-xs text-blue-800 leading-relaxed">
                              {t("createNewContactConfirm")}
                            </p>
                          </>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={onCreateContact}
                          className="text-xs font-medium px-2.5 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                        >
                          {t("yesCreateNew")}
                        </button>
                        <button
                          type="button"
                          onClick={createContact.cancel}
                          className="text-xs font-medium px-2.5 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          {t("cancel")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </SearchableSelect>

          {/* Name mismatch warning */}
          {hasNameMismatch && workerScenario?.kind !== "create-contact" && (
            <div className="mt-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-300">
              <AlertTriangle
                size={14}
                className="text-amber-600 shrink-0"
              />
              <p className="text-xs text-amber-800">
                {(() => {
                  const src = sourceLabel ?? t("source")
                  const raw = t("nameMismatchWarning", { source: src, name: "@@NAME@@" })
                  const [before, after = ""] = raw.split("@@NAME@@")
                  return (
                    <>
                      {before}
                      <span className="font-medium">{ats.workerName}</span>
                      {after}
                    </>
                  )
                })()}
              </p>
            </div>
          )}

        </div>
        )}

        {/* Worker Organization — locked-placeholder branch when no worker is picked yet.
            Mirrors the Worker field's pattern above ("Enter and confirm email address first"
            / "Select profile type first") so users see why the field is unavailable instead
            of being able to interact with it before the upstream selection is made. The
            search input would otherwise be live and the user could pick / create an org for
            a not-yet-selected worker, leaving them with mismatched form state. */}
        {isWorkerOrgVisible && !workerScenario && (
          <div id={`${FIELD_ANCHOR_ID_PREFIX}workerOrgId`} className="rounded-lg border border-gray-200 p-4 flex flex-col gap-3">
            <Label className="text-sm font-medium text-gray-700">
              {t("labelWorkerOrganization")}
              {isWorkerOrgRequired && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            <div className="flex items-center gap-2 h-9 px-3 rounded-md border border-dashed border-gray-200 bg-gray-50">
              <Lock size={14} className="text-amber-500 shrink-0" />
              <span className="text-sm text-gray-400">{t("selectWorkerFirst")}</span>
            </div>
          </div>
        )}

        {/* Worker Organization — active branch (worker selected) */}
        {isWorkerOrgVisible && workerScenario && (
          <div id={`${FIELD_ANCHOR_ID_PREFIX}workerOrgId`} className="rounded-lg border border-gray-200 p-4 flex flex-col gap-3">
            {/* Section header \u2014 rendered ABOVE the toggle so the field name is the first thing
                the user reads. SearchableSelect below uses `hideLabel` to avoid duplicating it. */}
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-sm font-medium text-gray-700">
                Worker Organization
                {isWorkerOrgRequired && <span className="text-red-500 ml-0.5">*</span>}
              </Label>
              <AtsHint value={ats.workerOrgName} />
              {workerOrgChanged && !isWorkerOrgUnknown && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                  {t("changed")}
                </span>
              )}
              {isWorkerOrgLocked && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                  <Lock size={10} />
                  {t("locked")}
                </span>
              )}
              {isWorkerOrgLocked && (
                <button
                  type="button"
                  onClick={onUnlockWorkerOrg}
                  className="ml-auto p-1 rounded transition-colors text-blue-600 hover:text-blue-700"
                  aria-label={t("unlockWorkerOrg")}
                  title={t("unlockToChangeOrg")}
                >
                  <Unlock size={14} />
                </button>
              )}
            </div>

            {/* Mode toggle: "Search organization" is shown FIRST (and is the default) so the
                user always tries to find/create a real org before falling back to a
                placeholder. Visibility is gated by `workerOrgCanChooseUnknownMode`
                (create-contact, add-profile, or unlocked existing-profile). While the user
                is in the new-org editor (`newWorkerOrgName != null`), the radios stay
                visible but disabled — the user can't switch modes mid-creation without
                clearing the new org first. ✗ on the editor resets back to a clean state. */}
            {!isWorkerOrgLocked && workerOrgCanChooseUnknownMode && (() => {
              const radiosDisabled = newWorkerOrgName != null
              return (
                <div
                  role="radiogroup"
                  aria-label={t("workerOrgInputMode")}
                  className="grid grid-cols-2 gap-2"
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!isWorkerOrgUnknown}
                    disabled={radiosDisabled}
                    onClick={() => {
                      if (radiosDisabled) return
                      if (isWorkerOrgUnknown) onEnterWorkerOrgSearchMode()
                    }}
                    className={cn(
                      "flex items-start gap-2 px-3 py-2.5 rounded-md border text-left transition-colors",
                      radiosDisabled && "cursor-not-allowed opacity-60",
                      !isWorkerOrgUnknown
                        ? "bg-blue-50 border-blue-500 ring-1 ring-blue-500 text-blue-900"
                        : "bg-white border-gray-300 text-gray-700",
                      !radiosDisabled && (!isWorkerOrgUnknown
                        ? ""
                        : "hover:bg-gray-50 hover:border-gray-400"),
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-2",
                        !isWorkerOrgUnknown ? "border-blue-600" : "border-gray-400",
                      )}
                    >
                      {!isWorkerOrgUnknown && <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />}
                    </span>
                    <span className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium leading-tight">{t("searchOrganization")}</span>
                      <span className="text-[11px] leading-tight opacity-80">
                        {t("pickOrCreateRealOrg")}
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isWorkerOrgUnknown}
                    disabled={radiosDisabled}
                    onClick={() => {
                      if (radiosDisabled) return
                      if (!isWorkerOrgUnknown) onEnterWorkerOrgUnknownMode()
                    }}
                    className={cn(
                      "flex items-start gap-2 px-3 py-2.5 rounded-md border text-left transition-colors",
                      radiosDisabled && "cursor-not-allowed opacity-60",
                      isWorkerOrgUnknown
                        ? "bg-blue-50 border-blue-500 ring-1 ring-blue-500 text-blue-900"
                        : "bg-white border-gray-300 text-gray-700",
                      !radiosDisabled && (isWorkerOrgUnknown
                        ? ""
                        : "hover:bg-gray-50 hover:border-gray-400"),
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-2",
                        isWorkerOrgUnknown ? "border-blue-600" : "border-gray-400",
                      )}
                    >
                      {isWorkerOrgUnknown && <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />}
                    </span>
                    <span className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium leading-tight">{t("iDontKnow")}</span>
                      <span className="text-[11px] leading-tight opacity-80">
                        {t("createPlaceholder")}
                      </span>
                    </span>
                  </button>
                </div>
              )
            })()}

            {isWorkerOrgUnknown ? (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-300">
                  <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex flex-col gap-0.5 text-xs leading-relaxed">
                    <p className="text-amber-800">
                      {t("placeholderOrgWillBeCreated")}
                    </p>
                    {workerOrgPlaceholderName && (
                      <p className="text-amber-900">
                        {(() => {
                          const [before, after = ""] = t("placeholderName", { name: "@@N@@" }).split("@@N@@")
                          return (
                            <>
                              {before}
                              <span className="font-medium">{workerOrgPlaceholderName}</span>
                              {after}
                            </>
                          )
                        })()}
                      </p>
                    )}
                  </div>
                </div>
                <p className={cn("text-xs min-h-4", showError("workerOrgId") && errors.workerOrgId ? "text-red-500" : "text-gray-400")}>
                  {showError("workerOrgId") ? errors.workerOrgId : undefined}
                </p>
              </div>
            ) : newWorkerOrgName != null ? (
              <NewWorkerOrgEditor
                name={newWorkerOrgName}
                options={workerOrgSearch.options}
                isSearching={workerOrgSearch.loading}
                onRename={onCreateWorkerOrg}
                onTriggerSearch={workerOrgSearch.search}
                onSelectExisting={onSelectWorkerOrg}
                onCancel={onClearWorkerOrg}
              />
            ) : (
              <SearchableSelect
                key={workerOrgRefreshKey}
                label={t("labelWorkerOrganization")}
                hideLabel
                value={workerOrgId}
                onValueChange={(v) =>
                  v ? onSelectWorkerOrg(v) : onClearWorkerOrg()
                }
                onSearch={workerOrgSearch.search}
                options={workerOrgSearch.options}
                loading={workerOrgSearch.loading}
                placeholder={t("placeholderSelectWorkerOrg")}
                error={
                  showError("workerOrgId")
                    ? errors.workerOrgId
                    : undefined
                }
                required={isWorkerOrgRequired}
                locked={isWorkerOrgLocked}
                helperText={
                  workerScenario?.kind === "create-contact" && !isWorkerOrgRequired
                    ? t("workerOrgOptionalNote")
                    : undefined
                }
                onCreateNew={onCreateWorkerOrg}
                createNewLabel={t("createNewWorkerOrganization")}
                disableCreateNew={suppressCreateNewWorkerOrg}
                suggestedCreateName={ats.workerOrgName ?? undefined}
                sourceLabel={sourceLabel}
                confirmCreateWhenMatchesVisible
              />
            )}
            {/* Excluded-org warning. Reactive to the *current* selection: when the picked
                org is already linked to a same-type profile in this email's landscape, surface
                a non-blocking advisory + "Use existing profile" suggestion. The user can act
                on the suggestion or proceed with the pick anyway — there are legitimate cases
                for linking two profiles to one org, so we no longer hard-block. */}
            {workerOrgId != null && workerOrgExcludedIds.has(workerOrgId) && newWorkerOrgName == null && !isWorkerOrgUnknown && (() => {
              const matched = workerOrgSearch.options.find((o) => o.id === workerOrgId)
              const orgLabel = matched?.canonicalName ?? matched?.name ?? "this organization"
              const profileTypeLabel = profileTypes.find((pt) => pt.code === form.profileTypeCode)?.value
                ?? form.profileTypeCode
                ?? "profile"
              return (
                <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-300">
                  <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1 flex flex-col gap-2">
                    <p className="text-xs text-amber-800 leading-relaxed">
                      {(() => {
                        const raw = t("orgAlreadyHasProfileWarning", { org: "@@ORG@@", profileType: "@@PT@@" })
                        const orgParts = raw.split("@@ORG@@")
                        const ptParts = (orgParts[1] ?? "").split("@@PT@@")
                        return (
                          <>
                            {orgParts[0]}
                            <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-900 font-medium">{orgLabel}</span>
                            {ptParts.length > 1 ? (
                              <>
                                {ptParts[0]}
                                <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-900 font-medium">{profileTypeLabel}</span>
                                {ptParts[1]}
                              </>
                            ) : (
                              orgParts[1]
                            )}
                          </>
                        )
                      })()}
                    </p>
                    <button
                      type="button"
                      onClick={() => onUseExistingProfileForOrg(workerOrgId)}
                      className="self-start text-xs font-medium px-2.5 py-1 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                    >
                      {t("useExistingProfile")}
                    </button>
                  </div>
                </div>
              )
            })()}
          </div>
        )}

        {/* Section-level info / status banners — rendered at the END of Worker Setup so the
            user reads them as a summary AFTER they've made their selections, not as a header
            before they've done anything. The What-will-happen banner reflects the current
            state (worker scenario + org scenario) and the blocked-status banner is a hard
            advisory anchored to the bottom so it doesn't push form fields around. */}
        {(showBlockedBanner || workerScenario || newWorkerOrgName || isWorkerOrgUnknown) && (
          <div className="flex flex-col gap-2 pt-2">
            {(workerScenario || newWorkerOrgName || isWorkerOrgUnknown) && (
              <div className="rounded-lg bg-teal-50 border border-teal-200 px-3.5 py-3">
                <div className="flex items-start gap-2.5">
                  <Sparkles size={14} className="text-teal-500 mt-0.5 shrink-0" />
                  <div className="flex flex-col gap-1 flex-1">
                    <span className="text-[11px] font-medium text-teal-600 uppercase tracking-wide">
                      {t("whatWillHappen")}
                    </span>
                    {/* Each bullet pairs an entity-specific icon with a single inline-pill that
                        names the entity (highlighted) so the user can scan the summary visually
                        rather than reading every word. Icons:
                          - IdCard       → profile-related actions
                          - UserPlus     → new worker contact + profile combined
                          - Check        → re-using an existing selection
                          - Building2    → organization-related actions (color shifts: emerald
                            for "new", amber for "placeholder", teal for "existing")
                          - Link2        → adding to an already-existing org */}
                    <ul className="flex flex-col gap-1.5">
                      {workerScenario?.kind === "add-profile" && (
                        <li className="text-xs text-teal-800 flex items-center gap-2">
                          <IdCard size={14} className="text-emerald-600 shrink-0" />
                          <span>
                            <span className="px-1 py-0.5 rounded bg-emerald-100 text-emerald-800 font-medium">{t("newProfile")}</span>
                            {" "}{t("newProfileAddedToExistingContact")}
                          </span>
                        </li>
                      )}
                      {workerScenario?.kind === "create-contact" && (
                        <li className="text-xs text-teal-800 flex items-center gap-2">
                          <UserPlus size={14} className="text-emerald-600 shrink-0" />
                          <span>
                            <span className="px-1 py-0.5 rounded bg-emerald-100 text-emerald-800 font-medium">{t("newContact")}</span>
                            {" "}
                            {(() => {
                              const [before, after = ""] = t("newContactAndProfileWillBeCreated", { newProfile: "@@NP@@" }).split("@@NP@@")
                              return (
                                <>
                                  {before}
                                  <span className="px-1 py-0.5 rounded bg-emerald-100 text-emerald-800 font-medium">{t("newProfile").toLowerCase()}</span>
                                  {after}
                                </>
                              )
                            })()}
                          </span>
                        </li>
                      )}
                      {workerScenario?.kind === "existing-profile" && (
                        <li className="text-xs text-teal-800 flex items-center gap-2">
                          <Check size={14} className="text-teal-600 shrink-0" />
                          <span>
                            <span className="px-1 py-0.5 rounded bg-teal-100 text-teal-800 font-medium">{t("existingProfile")}</span>
                            {" "}{t("existingProfileAndContactWillBeUsed")}
                          </span>
                        </li>
                      )}
                      {newWorkerOrgName && (
                        <li className="text-xs text-teal-800 flex items-center gap-2">
                          <Building2 size={14} className="text-emerald-600 shrink-0" />
                          <span>
                            <span className="px-1 py-0.5 rounded bg-emerald-100 text-emerald-800 font-medium">{t("newOrganization")}</span>
                            {" — "}
                            <span className="font-semibold text-emerald-900">{newWorkerOrgName}</span>
                            {" "}{t("newWorkerOrgWillBeCreated")}
                            {/* Names what "create new" brings: the organization is built from the mapped
                                supplier record, not from the name alone. Absent when the tenant maps no
                                supplier data, so it never promises what will not arrive. */}
                            {(ats.workerOrgAtsFieldKeys?.length ?? 0) > 0 && (
                              <span className="text-teal-700">
                                {" "}{t("withAtsData")}{" "}
                                <span className="font-medium text-emerald-900">
                                  {ats.workerOrgAtsFieldKeys?.map((key) => t(key)).join(", ")}
                                </span>
                              </span>
                            )}
                          </span>
                        </li>
                      )}
                      {isWorkerOrgUnknown && isWorkerOrgVisible && (
                        <li className="text-xs text-teal-800 flex items-start gap-2">
                          <Building2 size={14} className="text-amber-600 shrink-0 mt-0.5" />
                          <span>
                            <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">{t("placeholderOrganization")}</span>
                            {" "}{t("newWorkerOrgWillBeCreated")}
                            {workerOrgPlaceholderName ? (
                              <>
                                {" — "}
                                <span className="font-semibold text-amber-900">{workerOrgPlaceholderName}</span>
                              </>
                            ) : null}
                          </span>
                        </li>
                      )}
                      {!newWorkerOrgName && !isWorkerOrgUnknown && workerOrgId && isWorkerOrgVisible && (
                        <li className="text-xs text-teal-800 flex items-center gap-2">
                          <Link2 size={14} className="text-teal-600 shrink-0" />
                          <span>
                            <span className="px-1 py-0.5 rounded bg-teal-100 text-teal-800 font-medium">{t("linkedToExistingOrg")}</span>
                          </span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {showBlockedBanner && (
              <div className="rounded-lg bg-red-50 border border-red-300 p-3.5 flex gap-3">
                <AlertTriangle
                  size={16}
                  className="text-red-600 shrink-0 mt-0.5"
                />
                <p className="text-xs text-red-800 leading-relaxed">
                  {(() => {
                    const [before, after = ""] = t("blockedPendingProfile", { action: "@@A@@" }).split("@@A@@")
                    return (
                      <>
                        {before}
                        {blockedProfileUrl ? (
                          <a
                            href={blockedProfileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-red-900 underline underline-offset-2 hover:text-red-700"
                          >
                            {t("clickHere")}
                          </a>
                        ) : (
                          <span className="font-medium text-red-900">
                            {t("openPendingProfileInFbo")}
                          </span>
                        )}
                        {after}
                      </>
                    )
                  })()}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * Read-only Preferred Language warning copy — splits the translated string on placeholder
 * sentinels and injects amber-pill spans around the language names so they pop visually.
 * Three message shapes:
 *   1. Mismatch (FBO + request both set, disagree) — two pills.
 *   2. FBO has a value, no mismatch — one pill.
 *   3. FBO has no value at all — plain "managed in FBO" string, no pills.
 */
interface ReadOnlyLanguageWarningProps {
  fboCultureCode: string | null
  requestCultureCode: string
  hasMismatch: boolean
  labelFor: (code: string | null) => string
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string
}

function ReadOnlyLanguageWarning({
  fboCultureCode,
  requestCultureCode,
  hasMismatch,
  labelFor,
  t,
}: ReadOnlyLanguageWarningProps) {
  const Pill = ({ code }: { code: string | null }) => (
    <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-900 font-semibold">
      {labelFor(code)}
    </span>
  )

  if (hasMismatch) {
    const raw = t("preferredLanguageReadOnlyMismatch", {
      fboLanguage: "@@FBO@@",
      requestLanguage: "@@REQ@@",
    })
    const [beforeFbo, afterFbo = ""] = raw.split("@@FBO@@")
    const [middle, afterReq = ""] = afterFbo.split("@@REQ@@")
    return (
      <>
        {beforeFbo}
        <Pill code={fboCultureCode} />
        {middle}
        <Pill code={requestCultureCode} />
        {afterReq}
      </>
    )
  }
  if (fboCultureCode) {
    const [before, after = ""] = t("preferredLanguageReadOnly", { language: "@@LANG@@" }).split("@@LANG@@")
    return (
      <>
        {before}
        <Pill code={fboCultureCode} />
        {after}
      </>
    )
  }
  return <>{t("preferredLanguageManagedInFbo")}</>
}

/**
 * Pending-language-change confirmation body — splits the translated string on
 * `{current}` / `{pending}` sentinels and injects blue-pill spans (matching the
 * confirmation panel's color palette) around the two language names.
 */
interface PendingLanguageChangeBodyProps {
  currentCode: string
  pendingCode: string
  labelFor: (code: string | null) => string
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string
}

function PendingLanguageChangeBody({
  currentCode,
  pendingCode,
  labelFor,
  t,
}: PendingLanguageChangeBodyProps) {
  const Pill = ({ code }: { code: string }) => (
    <span className="px-1 py-0.5 rounded bg-blue-100 text-blue-900 font-semibold">
      {labelFor(code)}
    </span>
  )
  const raw = t("preferredLanguageChangeConfirmBody", {
    current: "@@CUR@@",
    pending: "@@PEN@@",
  })
  const [beforeCur, afterCur = ""] = raw.split("@@CUR@@")
  const [middle, afterPen = ""] = afterCur.split("@@PEN@@")
  return (
    <>
      {beforeCur}
      <Pill code={currentCode} />
      {middle}
      <Pill code={pendingCode} />
      {afterPen}
    </>
  )
}

/**
 * Embedded "Confirm new worker organization" editor — replaces the in-popover edit step that
 * lived inside SearchableSelect. Lifted to the form so the user can rename without
 * re-opening anything: as they type, we re-search and reconcile the typed name against the
 * search results to detect exact-name conflicts. Same email-style ✓/✗ controls as the
 * email-confirmation field, for consistency. Wired by the parent: ✗ clears workerOrgScenario,
 * "Use existing" pivots from kind:"new" to kind:"existing" without losing the rest of state.
 */
interface NewWorkerOrgEditorProps {
  name: string
  options: SearchableSelectOption[]
  /** True while an org search triggered by the latest rename is still in flight. Gates
   * confirmation so a user can't Enter/✓ past a name that's about to resolve to an exact match. */
  isSearching: boolean
  onRename: (next: string) => void
  onTriggerSearch: (term: string) => void
  onSelectExisting: (orgId: string) => void
  onCancel: () => void
}

function NewWorkerOrgEditor({
  name,
  options,
  isSearching,
  onRename,
  onTriggerSearch,
  onSelectExisting,
  onCancel,
}: NewWorkerOrgEditorProps) {
  const { t } = useTranslation()
  // State (confirmed/editing toggle, autofocus effect, trimmed/exactMatch/canConfirm
  // derivations including the in-flight-search guard) lives in the hook so this component
  // stays render-only.
  const { inputRef, confirmed, setConfirmed, trimmed, exactMatch, canConfirm } = useNewWorkerOrgEditor({
    name,
    options,
    isSearching,
  })

  if (confirmed) {
    return (
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium text-gray-700">
          {t("labelConfirmNewWorkerOrg")}
        </Label>
        <div className="flex items-center gap-2 h-9 px-3 rounded-md border border-gray-200 bg-gray-50">
          <Check size={14} className="text-emerald-600 shrink-0" />
          <span className="text-sm text-gray-700 flex-1 truncate">{trimmed}</span>
          <button
            type="button"
            onClick={() => setConfirmed(false)}
            className="p-1 rounded transition-colors text-gray-400 hover:text-gray-600"
            aria-label={t("editNewWorkerOrgName")}
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 rounded transition-colors text-red-500 hover:text-red-700"
            aria-label={t("cancelNewWorkerOrg")}
          >
            <X size={14} />
          </button>
        </div>
        <p className="text-xs text-emerald-700 min-h-4">
          {t("newOrgWillBeCreated", { name: trimmed })}
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-sm font-medium text-gray-700">
        Confirm new worker organization
      </Label>
      <div
        className={cn(
          "flex items-center rounded-md border bg-white",
          exactMatch ? "border-red-300" : "border-input",
        )}
      >
        <input
          ref={inputRef}
          value={name}
          onChange={(e) => {
            const next = e.target.value
            // Live state stays untrimmed so the user can naturally type spaces between words;
            // the search and the commit-time normalization handle whitespace explicitly.
            onRename(next)
            // Refresh options so the conflict check below reflects the server's actual list
            // for the just-typed string. Trim before hitting the index so trailing whitespace
            // doesn't cause false negatives that let a duplicate name through.
            onTriggerSearch(next.trim())
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canConfirm) {
              e.preventDefault()
              // Normalize the persisted name on commit — the typed value may include leading
              // or trailing whitespace; what gets created downstream should not.
              if (name !== trimmed) onRename(trimmed)
              setConfirmed(true)
            } else if (e.key === "Escape") {
              e.preventDefault()
              onCancel()
            }
          }}
          placeholder={t("placeholderEnterName")}
          className="flex-1 h-9 px-3 text-sm bg-transparent outline-none border-0 placeholder:text-muted-foreground"
        />
        <button
          type="button"
          onClick={() => {
            if (!canConfirm) return
            if (name !== trimmed) onRename(trimmed)
            setConfirmed(true)
          }}
          disabled={!canConfirm}
          className={cn(
            "h-7 w-7 my-1 flex items-center justify-center rounded transition-colors shrink-0",
            canConfirm
              ? "text-green-600 hover:bg-gray-100"
              : "text-gray-300 cursor-not-allowed",
          )}
          aria-label={t("confirmNewWorkerOrgName")}
        >
          <Check size={16} />
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-7 w-7 my-1 mr-1 flex items-center justify-center rounded transition-colors text-red-500 hover:bg-gray-100 shrink-0"
          aria-label="Cancel new worker organization"
        >
          <X size={16} />
        </button>
      </div>
      {!trimmed ? (
        <p className="text-xs text-gray-500 min-h-4">{t("nameCannotBeEmpty")}</p>
      ) : exactMatch ? (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200">
          <AlertTriangle size={14} className="text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1 flex flex-col gap-2">
            <p className="text-xs text-red-700 leading-relaxed">
              {t("orgAlreadyExistsPickInstead", { name: exactMatch.name })}
            </p>
            <button
              type="button"
              onClick={() => onSelectExisting(exactMatch.id)}
              className="self-start text-xs font-medium px-2.5 py-1 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors"
            >
              {t("useExisting")}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-emerald-700 min-h-4">
          {t("newOrgWillBeCreated", { name: trimmed })}
        </p>
      )}
    </div>
  )
}
